import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Plus, MessageSquare, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { AIChat } from "@/components/AIChat";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function AskAI() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);

  const { data: sessions = [] } = useQuery({
    queryKey: ["ai-sessions"],
    queryFn: async () => (await api.get("/ai/sessions")).data,
  });

  const onSession = (sid) => {
    setSessionId(sid);
    qc.invalidateQueries({ queryKey: ["ai-sessions"] });
  };

  const del = async (e, sid) => {
    e.stopPropagation();
    await api.delete(`/ai/sessions/${sid}`);
    if (sid === sessionId) setSessionId(null);
    qc.invalidateQueries({ queryKey: ["ai-sessions"] });
  };

  return (
    <div className="h-[calc(100vh-8.5rem)] flex flex-col">
      <PageHeader title="Ask AI" subtitle="Ask questions over live CRM data." />
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="hidden lg:flex flex-col overflow-hidden" data-testid="ai-sessions-list">
          <Button size="sm" variant="ghost" onClick={() => setSessionId(null)} className="gap-1.5 mb-2 justify-start text-slate-600 hover:text-slate-900" data-testid="ai-new-chat">
            <Plus className="h-4 w-4" /> New conversation
          </Button>
          <div className="flex-1 overflow-y-auto space-y-0.5">
            {sessions.map((s) => (
              <button key={s.id} onClick={() => setSessionId(s.id)}
                className={`w-full text-left px-2.5 py-2 rounded-md text-sm flex items-start gap-2 group transition-colors ${s.id === sessionId ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}
                data-testid={`ai-session-${s.id}`}>
                <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-300" />
                <span className="flex-1 truncate">{s.title}</span>
                <span onClick={(e) => del(e, s.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500" data-testid={`ai-session-del-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
            {sessions.length === 0 && <p className="text-xs text-slate-400 px-2.5 pt-1">No conversations yet.</p>}
          </div>
        </div>
        <Card className="lg:col-span-3 flex flex-col overflow-hidden shadow-none border-slate-200/80 bg-white" data-testid="ai-chat-panel">
          <AIChat sessionId={sessionId} onSession={onSession} />
        </Card>
      </div>
    </div>
  );
}
